// PollVote.jsx

import { useEffect, useMemo, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Services
import { getPollVoteCount } from 'services/polls/PollReadService';
import { castPollVote } from 'services/polls/PollWriteService';

// CSS
import './PollVote.css';

/**
 * PollVote
 *
 * Displays an interactive voting UI for a poll.
 *
 * @param {object} node - The node object.
 * @param {Function} onVote - Callback when a vote is cast.
 * @returns {ReactNode}
 */
const PollVote = ({ node, onVote }) => {

    // ----- CONTEXT ----- //

    // Global Context

    const { user } = useUser();

    // ----- STATE ----- //

    // State

    const [selectedIndex, setSelectedIndex] = useState(null);
    const [voteCount, setVoteCount] = useState(null);

    const title = node?.title || '';
    const choices = node?.pollinfo?.options || [];

    // ----- EFFECTS ----- //

    /**
     * Fetches the current vote count for the poll node.
     */

    useEffect(() => {
        const fetchVotes = async () => {
            try {
                const count = await getPollVoteCount(node.key);
                setVoteCount(count);
            } catch (err) {
                console.error('Failed to fetch vote count:', err);
            }
        };

        if (node?.key) {
            fetchVotes();
        }
    }, [node?.key]);

    // ----- MEMOS ----- //

    // Compute time remaining

    const durationRemaining = useMemo(() => {
        const endsAt = node?.pollinfo?.endsAt;
        if (!endsAt || typeof endsAt.toMillis !== 'function') return '';
        const msRemaining = endsAt.toMillis() - Date.now();

        if (msRemaining <= 0) return 'Final results';

        const minutes = Math.floor(msRemaining / (60 * 1000)) % 60;
        const hours = Math.floor(msRemaining / (60 * 60 * 1000)) % 24;
        const days = Math.floor(msRemaining / (24 * 60 * 60 * 1000));

        const parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? 's' : ''}`);
        if (minutes > 0 && days === 0) parts.push(`${minutes} min`);
        return parts.length > 0 ? `${parts.join(' ')} left` : 'Less than a minute left';
    }, [node?.pollinfo?.endsAt]);

    // ----- HANDLERS ----- //

    /**
     * handleVote
     *
     * Handles vote.
     *
     * @param {number} index - index.
     * @returns {Promise<void>} Resolves when the handler completes.
     */
    const handleVote = async (index) => {
        setSelectedIndex(index);

        if (!user?.key) {
            console.error('Cannot vote: no user loaded.');
            return;
        }

        try {
            await castPollVote({ pollNodeKey: node.key, userKey: user.key, optionIndex: index });

            if (onVote) onVote(index);

        } catch (error) {
            console.error('Error saving vote:', error);
        }
    };

    // ----- RENDER ----- //

    return (
        <div className="poll-vote-container">
            {/* Poll Title */}

            {/* TITLE */}
            <div className="poll-vote-title">{title}</div>

            {/* Choices */}

            {/* OPTIONS */}
            <div className="poll-vote-options">
                {choices.map((choice, index) => (
                    <button
                      className={`poll-vote-option ${selectedIndex === index ? 'selected' : ''}`}
                      key={index}
                      disabled={selectedIndex !== null}
                      onClick={() => handleVote(index)}
                    >
                        {choice}
                    </button>
                ))}
            </div>

            {/* Footer with mock vote count + time */}

            {/* FOOTER */}
            <div className="poll-vote-footer">
                <span className="poll-vote-count">
                    {voteCount === null ? '' : `${voteCount} vote${voteCount !== 1 ? 's' : ''}`}
                </span>
                {durationRemaining &&
                    <>
                        <span className="poll-vote-divider">•</span>
                        <span className="poll-vote-time">{durationRemaining}</span>
                    </>
                }
            </div>
        </div>
    );
};

// Dev Tools
PollVote.displayName = 'PollVote';

export default PollVote;
